(() => {
  const STORAGE_KEY = 'orkideWishlist';
  const TOGGLE_SELECTOR = '[data-wishlist-toggle]';
  const COUNT_SELECTOR = '[data-wishlist-count], .mobile-bottom-nav__badge--wishlist';
  const COUNT_LABEL_SELECTOR = '[data-wishlist-count-label]';
  const PAGE_SELECTOR = '[data-wishlist-page]';
  const PAGE_ITEMS_SELECTOR = '[data-wishlist-items]';
  const PAGE_SUMMARY_SELECTOR = '[data-wishlist-summary]';
  const SHARE_SELECTOR = '[data-wishlist-share]';
  const CLEAR_SELECTOR = '[data-wishlist-clear]';
  const statusTimers = new WeakMap();
  let lastTouchToggleAt = 0;

  const safeJsonParse = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  };

  const readWishlist = () => {
    try {
      if (!window.localStorage) return [];
      return safeJsonParse(window.localStorage.getItem(STORAGE_KEY), []).filter(
        (item) => item && typeof item === 'object' && item.id
      );
    } catch (_error) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    try {
      if (!window.localStorage) return;
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (_error) {
      // If storage is unavailable, keep the UI stable without throwing.
    }
  };

  const getProductPayload = (button) => {
    const { productId, productHandle, productUrl, productTitle, productImage } = button.dataset;
    if (!productId) return null;

    return {
      id: String(productId),
      handle: productHandle || '',
      url: productUrl || '',
      title: productTitle || '',
      image: productImage || '',
      addedAt: new Date().toISOString(),
    };
  };

  const isWishlisted = (productId) => readWishlist().some((item) => item.id === String(productId));

  const formatCount = (count) => (count > 99 ? '99' : String(count));
  const escapeHtml = (value) =>
    String(value || '').replace(/[&<>"']/g, (character) => {
      switch (character) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#39;';
        default:
          return character;
      }
    });
  const pluralize = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;
  const getProductUrl = (item) => item.url || (item.handle ? `/products/${item.handle}` : '#');
  const getProductImageMarkup = (item) => {
    const title = escapeHtml(item.title || 'Wishlist item');

    if (!item.image) {
      return '<span class="orkide-wishlist-card__placeholder" aria-hidden="true">O</span>';
    }

    return `
      <img
        src="${escapeHtml(item.image)}"
        alt="${title}"
        loading="lazy"
        width="420"
        height="525"
      >
    `;
  };

  const setTemporaryButtonLabel = (button, label) => {
    if (!button) return;

    const labelNode = button.querySelector('span:last-child') || button;
    const previousTimer = statusTimers.get(button);

    if (!button.dataset.defaultLabel) {
      button.dataset.defaultLabel = labelNode.textContent.trim();
    }

    if (previousTimer) {
      window.clearTimeout(previousTimer);
    }

    labelNode.textContent = label;

    const nextTimer = window.setTimeout(() => {
      labelNode.textContent = button.dataset.defaultLabel || labelNode.textContent;
      statusTimers.delete(button);
    }, 1800);

    statusTimers.set(button, nextTimer);
  };

  const setButtonState = (button, active) => {
    const title = button.dataset.productTitle || 'product';
    const addLabel = button.dataset.labelAdd || 'Add to Wishlist';
    const addedLabel = button.dataset.labelAdded || 'Added to Wishlist';
    const nextText = active ? addedLabel : addLabel;

    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${title} ${active ? 'from' : 'to'} wishlist`);

    if (button.classList.contains('orkide-product-wishlist-button')) {
      button.textContent = nextText;
    }
  };

  const syncButtons = (scope = document) => {
    const buttons = scope.querySelectorAll ? scope.querySelectorAll(TOGGLE_SELECTOR) : [];
    buttons.forEach((button) => {
      const productId = button.dataset.productId;
      if (!productId) return;
      setButtonState(button, isWishlisted(productId));
    });
  };

  const syncCountBadges = () => {
    const count = readWishlist().length;
    const badges = document.querySelectorAll(COUNT_SELECTOR);
    const labels = document.querySelectorAll(COUNT_LABEL_SELECTOR);

    badges.forEach((badge) => {
      badge.dataset.countValue = String(count);

      if (count > 0) {
        badge.textContent = formatCount(count);
        badge.classList.remove('is-hidden', 'is-empty');
      } else {
        badge.textContent = '';
        badge.classList.add('is-hidden', 'is-empty');
      }
    });

    labels.forEach((label) => {
      label.textContent = `${count} item${count === 1 ? '' : 's'}`;
    });
  };

  const renderWishlistItems = (items) => {
    if (!items.length) {
      return `
        <div class="orkide-wishlist-empty">
          <p class="orkide-wishlist-empty__eyebrow">Wishlist empty</p>
          <h2 class="orkide-wishlist-empty__title">Your saved favorites will appear here.</h2>
          <p class="orkide-wishlist-empty__text">
            Tap the heart on any product card to save it for later and keep your shortlist in one polished place.
          </p>
          <a class="orkide-wishlist-card__button orkide-wishlist-card__button--primary" href="/collections/all">
            Continue shopping
          </a>
        </div>
      `;
    }

    return `
      <div class="orkide-wishlist-page__grid">
        ${items
          .map((item) => {
            const title = escapeHtml(item.title || 'Wishlist item');
            const productUrl = escapeHtml(getProductUrl(item));
            const productHandle = escapeHtml(item.handle || '');
            const productImage = escapeHtml(item.image || '');

            return `
              <article class="orkide-wishlist-card" data-wishlist-item-id="${escapeHtml(item.id)}">
                <a class="orkide-wishlist-card__media" href="${productUrl}" aria-label="${title}">
                  ${getProductImageMarkup(item)}
                </a>
                <div class="orkide-wishlist-card__body">
                  <p class="orkide-wishlist-card__eyebrow">Saved item</p>
                  <h2 class="orkide-wishlist-card__title">
                    <a href="${productUrl}">${title}</a>
                  </h2>
                  <p class="orkide-wishlist-card__price">Ask for price</p>
                  <div class="orkide-wishlist-card__actions">
                    <a class="orkide-wishlist-card__button orkide-wishlist-card__button--primary" href="${productUrl}">
                      View product
                    </a>
                    <button
                      class="orkide-wishlist-card__button orkide-wishlist-card__button--secondary is-active"
                      type="button"
                      aria-label="Remove ${title} from wishlist"
                      aria-pressed="true"
                      data-wishlist-toggle
                      data-label-add="Remove"
                      data-label-added="Remove"
                      data-product-id="${escapeHtml(item.id)}"
                      data-product-handle="${productHandle}"
                      data-product-url="${productUrl}"
                      data-product-title="${title}"
                      data-product-image="${productImage}"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </article>
            `;
          })
          .join('')}
      </div>
    `;
  };

  const renderWishlistSummary = (items) => {
    const count = items.length;

    return `
      <div class="orkide-wishlist-summary-card">
        <p class="orkide-wishlist-summary-card__eyebrow">Wishlist overview</p>
        <h2 class="orkide-wishlist-summary-card__title">Keep your favorite pieces close.</h2>
        <div class="orkide-wishlist-summary-card__metrics">
          <div class="orkide-wishlist-summary-card__metric">
            <strong>${count}</strong>
            <span>${pluralize(count, 'saved item', 'saved items')}</span>
          </div>
          <div class="orkide-wishlist-summary-card__metric">
            <strong>1</strong>
            <span>private shortlist</span>
          </div>
        </div>
        <p class="orkide-wishlist-summary-card__text">
          Open any saved product to review details, choose your size, and continue with checkout when you are ready.
        </p>
        <div class="orkide-wishlist-summary-card__actions">
          <a class="orkide-wishlist-card__button orkide-wishlist-card__button--primary" href="/collections/all">
            Continue shopping
          </a>
          <button class="orkide-wishlist-card__button orkide-wishlist-card__button--secondary" type="button" data-wishlist-clear>
            Clear wishlist
          </button>
        </div>
      </div>
    `;
  };

  const renderWishlistPage = () => {
    const page = document.querySelector(PAGE_SELECTOR);
    if (!page) return;

    const itemsContainer = page.querySelector(PAGE_ITEMS_SELECTOR);
    const summaryContainer = page.querySelector(PAGE_SUMMARY_SELECTOR);
    if (!itemsContainer || !summaryContainer) return;

    const items = readWishlist();

    page.classList.toggle('is-empty', items.length === 0);
    itemsContainer.innerHTML = renderWishlistItems(items);
    summaryContainer.innerHTML = renderWishlistSummary(items);
    syncButtons(page);
  };

  const syncUi = (scope = document) => {
    syncButtons(scope);
    syncCountBadges();
    renderWishlistPage();
  };

  const dispatchWishlistUpdate = (detail) => {
    document.dispatchEvent(new CustomEvent('orkide:wishlist:updated', { detail }));
  };

  const stopToggleEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
  };

  const toggleWishlist = (button) => {
    const payload = getProductPayload(button);
    if (!payload) return;

    const items = readWishlist();
    const existingIndex = items.findIndex((item) => item.id === payload.id);

    if (existingIndex >= 0) {
      items.splice(existingIndex, 1);
      writeWishlist(items);
      syncUi(document);
      dispatchWishlistUpdate({ productId: payload.id, active: false, items, count: items.length });
      return;
    }

    items.unshift(payload);
    writeWishlist(items);
    syncUi(document);
    dispatchWishlistUpdate({ productId: payload.id, active: true, items, count: items.length });
  };

  const clearWishlist = (button) => {
    const items = readWishlist();
    if (!items.length) {
      setTemporaryButtonLabel(button, 'Already empty');
      return;
    }

    if (typeof window.confirm === 'function' && !window.confirm('Clear all saved items from your wishlist?')) {
      return;
    }

    writeWishlist([]);
    syncUi(document);
    dispatchWishlistUpdate({ productId: null, active: false, items: [], count: 0 });
    setTemporaryButtonLabel(button, 'Wishlist cleared');
  };

  const shareWishlist = async (button) => {
    const shareData = {
      title: document.title,
      text: 'Take a look at my Orkide wishlist.',
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareData.url);
        setTemporaryButtonLabel(button, 'Link copied');
        return;
      }
    } catch (error) {
      if (error && error.name === 'AbortError') return;
    }

    setTemporaryButtonLabel(button, 'Share unavailable');
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.pointerType === 'mouse') return;

      const button = event.target.closest(TOGGLE_SELECTOR);
      if (!button) return;

      stopToggleEvent(event);
    },
    { capture: true, passive: false }
  );

  document.addEventListener(
    'touchend',
    (event) => {
      const button = event.target.closest(TOGGLE_SELECTOR);
      if (!button) return;

      lastTouchToggleAt = Date.now();
      stopToggleEvent(event);
      toggleWishlist(button);
    },
    { capture: true, passive: false }
  );

  document.addEventListener(
    'click',
    (event) => {
      const shareButton = event.target.closest(SHARE_SELECTOR);
      if (shareButton) {
        event.preventDefault();
        void shareWishlist(shareButton);
        return;
      }

      const clearButton = event.target.closest(CLEAR_SELECTOR);
      if (clearButton) {
        event.preventDefault();
        clearWishlist(clearButton);
        return;
      }

      const button = event.target.closest(TOGGLE_SELECTOR);
      if (!button) return;

      if (Date.now() - lastTouchToggleAt < 450) {
        stopToggleEvent(event);
        return;
      }

      stopToggleEvent(event);
      toggleWishlist(button);
    },
    true
  );

  const initialize = () => syncUi(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => syncUi(event.target));
  document.addEventListener('shopify:section:reorder', () => syncUi(document));
  document.addEventListener('orkide:wishlist:updated', () => syncCountBadges());
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      syncUi(document);
    }
  });
})();
